import { CSSProperties, FC, useMemo, useRef } from 'react'

import { Breakpoints } from '@hhgtech/hhg-components'
import { FontWeight } from '@hhgtech/hhg-components/mantine'
import { Scale } from '@hhgtech/icons/other'
import {
  Box,
  Grid,
  MantineTheme,
  rem,
  ScrollArea,
  SimpleGrid,
  Stack,
  useMantineTheme,
} from '@mantine/core'
import { useIntl } from 'react-intl'

import { getHelloSitesUrl } from 'api/utils/paths'
import { AirPopulationEmbedCard } from 'components/molecules/AirPoulationEmbed'
import FeatureCard from 'components/molecules/FeatureCard'
import { FeatureCardLayout } from 'components/molecules/FeatureCard/types'
import {
  BabyPoopCard,
  BabyPoopCardWrapperProps,
} from 'components/organisms/embededHeathToolCards/baby-poop'
import {
  BabyVaccinationCard,
  BabyVaccineCardWrapperProps,
} from 'components/organisms/embededHeathToolCards/baby-vaccination'
import {
  BabyGrowthCard,
  BabyGrowthCardWrapperProps,
} from 'components/organisms/embededHeathToolCards/embedBabyGrowth'
import {
  BMICardWrapper,
  BMICardWrapperProps,
} from 'components/organisms/embededHeathToolCards/embedBMI'
import { BMRCardWrapper } from 'components/organisms/embededHeathToolCards/embedBMR'
import {
  DueDateCardWrapper,
  DueDateCardWrapperProps,
} from 'components/organisms/embededHeathToolCards/embedDueDate'
import {
  OvulationCardWrapper,
  OvulationCardWrapperProps,
} from 'components/organisms/embededHeathToolCards/embedOvulation'
import {
  PWGCardWrapper,
  PWGCardWrapperProps,
} from 'components/organisms/embededHeathToolCards/embedPwg'
import {
  THRCardWrapper,
  THRCardWrapperProps,
} from 'components/organisms/embededHeathToolCards/embedTHR'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { basePath } from 'interfaces/constants'
import { HEALTH_TOOL_NAME } from 'interfaces/constants/healthTools'
import { HealthTool } from 'interfaces/types'

import {
  StyledHealthToolCard,
  StyledOtherToolDesc,
  StyledOtherToolTitle,
} from './HomeHealthTool.styled'
import CarouselCard from '../CarouselCardV2'
import { GroupItems } from '../groupItems'

export type HomeHealthToolProps = {
  embedCard: HEALTH_TOOL_NAME
  healthTools: HealthTool[]
  className?: string
  style?: CSSProperties
}

const CAROOUSEL_ROW_BY_HEALTH_TOOL: {
  [key in HEALTH_TOOL_NAME]?: number
} = {
  [HEALTH_TOOL_NAME.BMI]: 3,
  [HEALTH_TOOL_NAME.BMR]: 2,
  [HEALTH_TOOL_NAME.BABY_GROWTH]: 2,
  [HEALTH_TOOL_NAME.DUE_DATE]: 2,
  [HEALTH_TOOL_NAME.OVULATION]: 2,
  [HEALTH_TOOL_NAME.THR]: 1,
  [HEALTH_TOOL_NAME.PWG]: 2,
  [HEALTH_TOOL_NAME.BABY_VACCINE]: 1,
  [HEALTH_TOOL_NAME.BABY_POOP]: 1,
  [HEALTH_TOOL_NAME.AIR_POPULATION]: 1,
}

export const HomeHealthTool: FC<HomeHealthToolProps> = (props) => {
  const { healthTools, embedCard } = props
  const $carousel = useRef<HTMLDivElement>(null)
  const theme = useMantineTheme()

  const { locale } = useRouterExtend()
  const { formatMessage: f } = useIntl()
  const isMobile = useIsMobile()

  const currentDomain = useMemo(() => getHelloSitesUrl(locale), [locale])
  const defaultItemsPerRow = useMemo(() => (isMobile ? 2 : 3), [isMobile])
  const defaultRowsPerSlide = useMemo(
    () => (isMobile ? 2 : CAROOUSEL_ROW_BY_HEALTH_TOOL[embedCard] || 2),
    [isMobile, embedCard],
  )

  const healthToolWrapperStyles = (theme: MantineTheme) => {
    return {
      root: {
        border: 0,
        borderRadius: 'inherit',
      },
      bodyWrapper: {
        [theme.fn.largerThan('sm')]: {
          padding: 16,
        },
      },
      bannerWrapper: {
        [theme.fn.largerThan('sm')]: {
          padding: `${rem(32)} ${rem(32)} 0`,
        },
      },
      submitWrapper: {
        [theme.fn.smallerThan('sm')]: {
          backgroundColor: '#fff',
          borderBottomLeftRadius: 24,
          borderBottomRightRadius: 24,
        },
        [theme.fn.largerThan('sm')]: {
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1,
          paddingRight: 32,
          paddingBottom: `${rem(32)} !important`,
        },
      },
      scrollAreaWrapper: {
        [theme.fn.largerThan('sm')]: {
          paddingBottom: 72,
        },
      },
      submitBtn: {
        borderRadius: 48,
      },
      newsletterBlock: {
        [theme.fn.smallerThan('sm')]: {
          paddingTop: 0,
          paddingBottom: 0,
          backgroundColor: 'transparent',
        },
      },
    }
  }
  const commonCardProps = {
    variant: 'gray',
    positionType: 'article',
    styles: healthToolWrapperStyles,
  }
  return (
    <>
      <div ref={$carousel} className="healthToolsCarouselResize" />
      <FeatureCard
        layout={FeatureCardLayout.VerticalCenter}
        url="/health-tools/"
        sx={(theme) => ({
          [theme.fn.smallerThan('sm')]: {
            backgroundColor: theme.colors.green[0],
            '.viewall-bottom': {
              paddingTop: 0,
            },
            '.card-content': {
              paddingBottom: 0,
            },
          },
        })}
      >
        <FeatureCard.SectionName
          icon={<Scale size={20} color={theme.colors.green[6]} />}
          data-event-category="Recommend Health Tools"
          data-event-action="See All Click"
          data-event-label={`${currentDomain}${basePath}/health-tools`}
        >
          {f({
            id: 'home.healthTools.iconTitle',
            defaultMessage: 'HEALTH TOOLS',
          })}
        </FeatureCard.SectionName>
        <FeatureCard.Heading>
          {f({
            id: 'home.healthTools.title',
            defaultMessage:
              'Công cụ đơn giản đánh giá các khía cạnh chính của sức khỏe.',
          })}
        </FeatureCard.Heading>
        <FeatureCard.ViewAll
          className="healthToolsButtonExplore"
          data-event-category="Recommend Health Tools"
          data-event-action="See All Click"
          data-event-label={`${currentDomain}${basePath}/health-tools`}
        >
          {f({
            id: 'home.healthTools.buttonViewAll',
            defaultMessage: 'Explore all Health tools',
          })}
        </FeatureCard.ViewAll>
        <FeatureCard.Content>
          <SimpleGrid
            spacing={24}
            cols={1}
            breakpoints={[{ minWidth: 'sm', cols: 2 }]}
            sx={(theme) => ({
              [theme.fn.smallerThan('sm')]: {
                paddingTop: 16,
                backgroundColor: theme.colors.green[0],
              },
            })}
          >
            <Box
              sx={(theme) => ({
                [theme.fn.smallerThan('sm')]: {
                  paddingLeft: 16,
                  paddingRight: 16,
                },
                [theme.fn.largerThan('sm')]: {
                  position: 'relative',
                  height: '100%',
                },
              })}
            >
              <Box
                sx={(theme) => ({
                  borderRadius: 16,
                  [theme.fn.smallerThan('sm')]: {
                    backgroundColor: '#fff',
                  },
                  [theme.fn.largerThan('sm')]: {
                    borderRadius: 24,
                    backgroundColor: theme.colors.gray[0],
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                  },
                })}
              >
                <ScrollArea
                  h="100%"
                  sx={{ borderRadius: 'inherit' }}
                  scrollbarSize={8}
                >
                  {(() => {
                    switch (embedCard) {
                      case HEALTH_TOOL_NAME.BMI:
                        return (
                          <BMICardWrapper
                            {...(commonCardProps as BMICardWrapperProps)}
                          />
                        )
                      case HEALTH_TOOL_NAME.BMR:
                        return (
                          <BMRCardWrapper
                            {...(commonCardProps as BMICardWrapperProps)}
                          />
                        )
                      case HEALTH_TOOL_NAME.PWG:
                        return (
                          <PWGCardWrapper
                            {...(commonCardProps as PWGCardWrapperProps)}
                          />
                        )
                      case HEALTH_TOOL_NAME.THR:
                        return (
                          <THRCardWrapper
                            {...(commonCardProps as THRCardWrapperProps)}
                          />
                        )
                      case HEALTH_TOOL_NAME.OVULATION:
                        return (
                          <OvulationCardWrapper
                            {...(commonCardProps as OvulationCardWrapperProps)}
                          />
                        )
                      case HEALTH_TOOL_NAME.DUE_DATE:
                        return (
                          <DueDateCardWrapper
                            {...(commonCardProps as DueDateCardWrapperProps)}
                          />
                        )
                      case HEALTH_TOOL_NAME.BABY_VACCINE:
                        return (
                          <BabyVaccinationCard
                            {...(commonCardProps as BabyVaccineCardWrapperProps)}
                          />
                        )
                      case HEALTH_TOOL_NAME.BABY_POOP:
                        return (
                          <BabyPoopCard
                            {...(commonCardProps as BabyPoopCardWrapperProps)}
                          />
                        )
                      case HEALTH_TOOL_NAME.BABY_GROWTH:
                        return (
                          <BabyGrowthCard
                            {...(commonCardProps as BabyGrowthCardWrapperProps)}
                          />
                        )
                      case HEALTH_TOOL_NAME.AIR_POPULATION:
                        return (
                          <AirPopulationEmbedCard
                            formProps={{
                              size: 'sm',
                              borderOutsideColor: theme.colors.gray[1],
                              backgroundChartColor: theme.colors.gray[0],
                              backgroundColor: isMobile
                                ? '#fff'
                                : theme.colors.gray[0],
                            }}
                            cardProps={{
                              styles: (theme) => ({
                                root: {
                                  border: 'transparent',
                                  background: 'transparent',
                                  '.chart-border-white': {
                                    backgroundColor: theme.colors.gray[0],
                                  },
                                  [theme.fn.largerThan('sm')]: {
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    gap: 16,
                                    '.air-population-body': {
                                      width: '100%',
                                    },
                                  },
                                },
                                bannerWrapper: {
                                  background: 'transparent',
                                  padding: 16,
                                  [theme.fn.largerThan('sm')]: {
                                    padding: '32px 32px 0',
                                  },
                                },
                                bodyWrapper: {
                                  padding: '0 16px 16px !important',
                                  [theme.fn.largerThan('sm')]: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '0 32px !important',
                                  },
                                },
                                bannerHeading: {
                                  fontSize: rem(20),
                                  lineHeight: rem(28),
                                  letterSpacing: '-0.4px',
                                  fontWeight: FontWeight.semiBold,
                                  [theme.fn.smallerThan('sm')]: {
                                    fontSize: rem(17),
                                    lineHeight: rem(24),
                                    letterSpacing: '-0.4px',
                                    fontWeight: FontWeight.semiBold,
                                  },
                                },
                              }),
                            }}
                            {...(commonCardProps as BabyGrowthCardWrapperProps)}
                          />
                        )
                      default:
                        return null
                    }
                  })()}
                </ScrollArea>
              </Box>
            </Box>
            <Stack
              spacing={16}
              sx={(theme) => ({
                padding: 16,
                [theme.fn.smallerThan('sm')]: {
                  paddingBottom: 0,
                },
                [theme.fn.largerThan('sm')]: {
                  backgroundColor: theme.colors.green[0],
                  borderRadius: 24,
                  padding: 32,
                },
              })}
            >
              <Stack
                spacing={8}
                sx={(theme) => ({
                  [theme.fn.smallerThan('sm')]: {
                    paddingBottom: 0,
                  },
                })}
              >
                <StyledOtherToolTitle>
                  {f({
                    id: 'home.healthTools.otherToolsTitle',
                    defaultMessage: 'Các công cụ sức khoẻ khác',
                  })}
                </StyledOtherToolTitle>
                <StyledOtherToolDesc>
                  {f({
                    id: 'healthtools.homeDescription',
                    defaultMessage:
                      'Các công cụ đánh giá sức khỏe dễ sử dụng này sẽ giúp bạn đánh giá các yếu tố quan trọng trong sức khỏe tổng quan và chất lượng sức khỏe của bạn.',
                  })}
                </StyledOtherToolDesc>
              </Stack>
              <CarouselCard
                align={'start'}
                slideSize={'100%'}
                slidesToScroll={1}
                slideGap={12}
                autoplay
                breakpoints={[
                  {
                    maxWidth: Breakpoints.BREAK_POINT_TABLET,
                    slideSize: '75%',
                    slideGap: 8,
                  },
                ]}
                styles={(theme) => ({
                  viewport: {
                    [theme.fn.smallerThan('sm')]: {
                      paddingLeft: 16,
                      paddingRight: 16,
                    },
                  },
                })}
              >
                <Stack
                  spacing={36}
                  sx={(theme) => ({
                    [theme.fn.largerThan('sm')]: {
                      minHeight: `${
                        140 * defaultRowsPerSlide +
                        24 * (defaultItemsPerRow - 1)
                      }px`,
                    },
                  })}
                >
                  <Box
                    sx={(theme) => ({
                      [theme.fn.smallerThan('sm')]: {
                        marginLeft: -16,
                        marginRight: -16,
                      },
                    })}
                  >
                    <CarouselCard.Body>
                      <GroupItems
                        breakpoints={{
                          base: {
                            itemsPerGroup: 4,
                            GroupWrapper: (children) => (
                              <CarouselCard.Slide>
                                <Grid gutter={8}>{children}</Grid>
                              </CarouselCard.Slide>
                            ),
                            ItemWrapper: (children) => (
                              <Grid.Col span={6}>{children}</Grid.Col>
                            ),
                          },
                          md: {
                            itemsPerGroup:
                              defaultItemsPerRow * defaultRowsPerSlide,
                            GroupWrapper: (children) => (
                              <CarouselCard.Slide>
                                <Grid gutter={12}>{children}</Grid>
                              </CarouselCard.Slide>
                            ),
                            ItemWrapper: (children) => (
                              <Grid.Col span={4}>{children}</Grid.Col>
                            ),
                          },
                        }}
                        items={healthTools.map((healthTool, index) => (
                          <StyledHealthToolCard
                            key={String(index)}
                            style={{ height: '100%' }}
                            item={{
                              icon: healthTool.icon,
                              title: healthTool.title,
                              permalink: healthTool.link,
                            }}
                            itemTracking={{
                              link: {
                                'data-event-category': 'Recommend Health Tools',
                                'data-event-action': 'Health Tools Click',
                                'data-event-label': healthTool.link.includes(
                                  'https://',
                                )
                                  ? healthTool.link
                                  : `${currentDomain}${basePath}${healthTool.link}`,
                              },
                            }}
                          />
                        ))}
                      />
                    </CarouselCard.Body>
                  </Box>
                  <CarouselCard.Footer>
                    <CarouselCard.Progress
                      styles={(theme) => ({
                        root: {
                          backgroundColor: 'rgba(0, 140, 80, 0.1)',
                        },
                        bar: {
                          backgroundColor: `${theme.colors.green[8]} !important`,
                        },
                      })}
                    />
                    <CarouselCard.Controls>
                      <CarouselCard.Control
                        direction="prev"
                        data-event-category="Recommend Health Tools"
                        data-event-action=" See More Health Tools"
                        data-event-label={`${currentDomain}${basePath}`}
                      />
                      <CarouselCard.Control
                        direction="next"
                        data-event-category={'Recommend Health Tools'}
                        data-event-action="See More Health Tools"
                        data-event-label={`${currentDomain}${basePath}`}
                      />
                    </CarouselCard.Controls>
                  </CarouselCard.Footer>
                </Stack>
              </CarouselCard>
            </Stack>
          </SimpleGrid>
        </FeatureCard.Content>
      </FeatureCard>
    </>
  )
}
